import { Autocomplete, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { type GeocodingFeature, searchAddress } from "@designboticteam/maplibre";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

type Props = {
	onSelect: (feature: GeocodingFeature) => void;
	placeholder?: string;
	size?: "small" | "medium";
	relevanceCenter?: [number, number];
};

const AddressSearch = ({ onSelect, placeholder = "Szukaj adresu...", size = "small", relevanceCenter }: Props): React.ReactElement => {
	const [input, setInput] = useState("");
	const [options, setOptions] = useState<Array<GeocodingFeature>>([]);
	const [isBusy, setIsBusy] = useState(false);

	useEffect(() => {
		if (input.trim().length < MIN_QUERY_LENGTH) {
			setOptions([]);
			return;
		}

		const controller = new AbortController();

		const timeoutId = setTimeout(async () => {
			setIsBusy(true);

			try {
				const results = await searchAddress({ query: input, signal: controller.signal, relevanceCenter });
				setOptions(results);
			} catch (error) {
				if ((error as Error).name !== "AbortError") {
					setOptions([]);
				}
			} finally {
				setIsBusy(false);
			}
		}, DEBOUNCE_MS);

		return () => {
			controller.abort();
			clearTimeout(timeoutId);
		};
	}, [input, relevanceCenter]);

	return (
		<Autocomplete
			freeSolo
			options={options}
			loading={isBusy}
			filterOptions={(x) => x}
			getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
			isOptionEqualToValue={(option, value) => typeof value !== "string" && option.id === value.id}
			onInputChange={(_, value) => setInput(value)}
			onChange={(_, value) => {
				if (value !== null && typeof value !== "string") {
					onSelect(value);
				}
			}}
			renderInput={(params) => <TextField {...params} size={size} placeholder={placeholder} />}
		/>
	);
};

export type { GeocodingFeature };
export { AddressSearch };
